/**
 * The Customize dialog's decisions (1.AC-2 deliverable 19, pure): which types it offers, what a
 * refusal marks, the request it sends and the answer it resolves.
 */
import {
  ApiError,
  type DocTypeSummaryV1,
  type VariantCreatedV1,
  type VariantCreateRequestV1,
} from '@codex/contracts';

/** Live, canon types with `has_sheet`, by name, the `creature` type first. */
export function sheetTypes(types: readonly DocTypeSummaryV1[]): DocTypeSummaryV1[] {
  return types
    .filter((t) => t.library === 'canon' && !t.archived && t.capabilities.includes('has_sheet'))
    .sort((a, b) => {
      if ((a.key === 'creature') !== (b.key === 'creature')) return a.key === 'creature' ? -1 : 1;
      return a.name.localeCompare(b.name, 'en');
    });
}

export interface CustomizeFields {
  readonly name: string;
  readonly typeId: string;
  readonly homeId: string | null;
  readonly repoint: boolean;
}

export interface CustomizeErrors {
  readonly name?: string;
  readonly type?: string;
  readonly home?: string;
  /** What belongs to no field. */
  readonly message?: string;
}

export const CUSTOMIZE_GENERIC = "Couldn't create the copy.";
export const EMPTY_NAME = 'Enter a name for the copy.';

/** 1.AC-1's table for `variants.create`, by field. */
export function customizeErrors(error: unknown): CustomizeErrors {
  if (!(error instanceof ApiError)) return { message: CUSTOMIZE_GENERIC };
  if (error.status === 409 && error.code === 'document_trashed') {
    return { home: 'That page is in the trash.' };
  }
  if (error.status === 400 && error.code === 'invalid_request') {
    return { name: 'Enter a name of up to 200 characters.' };
  }
  if (error.status === 422 && error.code === 'invalid_body') {
    const details = error.body.details;
    const issue =
      typeof details === 'object' && details !== null
        ? (details as { issue?: unknown }).issue
        : undefined;
    if (issue === 'type_without_sheet') return { type: "That type doesn't have stat blocks." };
    if (issue === 'unknown_type') return { type: 'That type is no longer available.' };
    if (issue === 'source_not_creature') {
      return { message: 'Homebrew spells and items come in a later release.' };
    }
  }
  return { message: CUSTOMIZE_GENERIC };
}

/** The body of `variants.create`; the name is trimmed, no home is left out. */
export function variantRequest(fields: CustomizeFields): VariantCreateRequestV1 {
  return {
    title: fields.name.trim(),
    typeId: fields.typeId,
    ...(fields.homeId === null ? {} : { homeId: fields.homeId }),
  };
}

export interface CustomizeResult {
  readonly documentId: string;
  readonly sheetId: string | null;
  readonly title: string;
  /** True only when the embed repoint was offered and left checked. */
  readonly repoint: boolean;
}

export function customizeResult(
  answer: VariantCreatedV1,
  fields: Pick<CustomizeFields, 'repoint'>,
  offerRepoint: boolean,
): CustomizeResult {
  return {
    documentId: answer.document.id,
    sheetId: answer.sheetId,
    title: answer.document.title,
    repoint: offerRepoint && fields.repoint,
  };
}
